// Company: Ajinsoft
// Author: Uilan Coqueiro
// Date: 2023-03-30
// Version: 3

import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AjinDialogBoxConfig } from './dialog-box.models';
import { AjinDialogBoxComponent } from './dialog-box.component';
import { merge } from 'lodash';
import { TranslocoService } from '@ngneat/transloco';

@Injectable({
	providedIn: 'root'
})
export class AjinDialogBoxService {
	private _translocoService = inject(TranslocoService);
	private _matDialog = inject(MatDialog);

	private _defaultConfig: AjinDialogBoxConfig = {
		title: this._translocoService.translate('Confirm'),
		message: this._translocoService.translate('Are you sure you want to confirm this action?'),
		icon: {
			name: '',
			color: 'primary'
		},
		confirm: {
			color: 'primary'
		},
		actions: 'yesNo',
		dismissible: true
	};

	private _deleteConfig: AjinDialogBoxConfig = {
		title: this._translocoService.translate('Confirm'),
		message: this._translocoService.translate('Are you sure you want to confirm this action?'),
		icon: {
			name: 'mat_outline:warning_amber',
			color: 'warn'
		},
		confirm: {
			color: 'warn'
		},
		actions: 'yesNo',
		dismissible: true
	};

	open(config: AjinDialogBoxConfig = {}): MatDialogRef<AjinDialogBoxComponent> {

		const userConfig = merge({}, this._defaultConfig, config);

		return this._matDialog.open(AjinDialogBoxComponent, {
			autoFocus: false,
			disableClose: !userConfig.dismissible,
			data: userConfig,
			panelClass: 'ajin-dialog-box'
		});
	}

	openDelete(config: AjinDialogBoxConfig = {}): MatDialogRef<AjinDialogBoxComponent> {
		const userConfig = merge({}, this._deleteConfig, config);

		if (!config?.title)
			userConfig.title = this._translocoService.translate('Delete');

		if (!config?.message)
			userConfig.message = this._translocoService.translate('Do you want to delete the selected item?');

		return this._matDialog.open(AjinDialogBoxComponent, {
			autoFocus: false,
			disableClose: !userConfig.dismissible,
			data: userConfig,
			panelClass: 'ajin-dialog-box'
		});
	}

	openCancel(config: AjinDialogBoxConfig = {}): MatDialogRef<AjinDialogBoxComponent> {
		const userConfig = merge({}, this._deleteConfig, config);

		if (!config?.title)
			userConfig.title = this._translocoService.translate('Cancel');

		if (!config?.message)
			userConfig.message = this._translocoService.translate('Do you want to cancel the selected item?');

		return this._matDialog.open(AjinDialogBoxComponent, {
			autoFocus: false,
			disableClose: !userConfig.dismissible,
			data: userConfig,
			panelClass: 'ajin-dialog-box'
		});
	}

	openDeleteMany(config: AjinDialogBoxConfig = {}): MatDialogRef<AjinDialogBoxComponent> {
		if (!config?.message)
			config.message = this._translocoService.translate('Do you want to delete the selected items?');
		return this.openDelete(config);
	}

	openCancelMany(config: AjinDialogBoxConfig = {}): MatDialogRef<AjinDialogBoxComponent> {
		if (!config?.message)
			config.message = this._translocoService.translate('Do you want to cancel the selected items?');
		return this.openCancel(config);
	}

	openDeleteByParent(config: AjinDialogBoxConfig = {}): MatDialogRef<AjinDialogBoxComponent> {
		const userConfig = merge({}, this._deleteConfig, config);

		if (!config?.title)
			userConfig.title = this._translocoService.translate('Delete');

		if (!config?.message)
			userConfig.message = this._translocoService.translate('Do you want to delete the selected item and ?');

		return this._matDialog.open(AjinDialogBoxComponent, {
			autoFocus: false,
			disableClose: !userConfig.dismissible,
			data: userConfig,
			panelClass: 'ajin-dialog-box'
		});
	}


	openRestore(config: AjinDialogBoxConfig = {}): MatDialogRef<AjinDialogBoxComponent> {
		const userConfig = merge({}, this._deleteConfig, config);

		if (!config?.title)
			userConfig.title = this._translocoService.translate('Restore');

		if (!config?.message)
			userConfig.message = this._translocoService.translate('Do you want to restore the selected item?');

		return this._matDialog.open(AjinDialogBoxComponent, {
			autoFocus: false,
			disableClose: !userConfig.dismissible,
			data: userConfig,
			panelClass: 'ajin-dialog-box'
		});
	}

	openRestoreMany(config: AjinDialogBoxConfig = {}): MatDialogRef<AjinDialogBoxComponent> {
		if (!config?.message)
			config.message = this._translocoService.translate('Do you want to restore the selected items?');
		return this.openRestore(config);
	}


	openInput(config: AjinDialogBoxConfig = {}): MatDialogRef<AjinDialogBoxComponent> {
		const userConfig = merge({}, this._defaultConfig, config);
		userConfig.type = 'input';

		return this._matDialog.open(AjinDialogBoxComponent, {
			autoFocus: false,
			disableClose: !userConfig.dismissible,
			data: userConfig,
			panelClass: 'ajin-dialog-box'
		});
	}
}
